/**
 * useMapFlying Hook - Centralized Map Flying Operations with Popup Management
 *
 * ARCHITECTURAL QUESTION: Is this hook necessary or is it adding unnecessary complexity?
 *
 * ARGUMENTS FOR KEEPING THIS HOOK:
 * ================================
 * 1. **Centralized Popup Management**: Automatically closes popups for all intentional navigation,
 *    ensuring consistent UX where users don't see stale popups when flying to new locations.
 *
 * 2. **Consistent API**: Provides a unified interface for all map flying operations with
 *    consistent default parameters (duration, zoom levels, etc.).
 *
 * 3. **Future Extensibility**: Easy to add features like loading states, animation callbacks,
 *    or analytics tracking to all flying operations in one place.
 *
 * 4. **Separation of Concerns**: Keeps popup management logic separate from individual
 *    components that just want to fly the map somewhere.
 *
 * 5. **Reusability**: Could be used by other components beyond MapComponent if needed
 *    (though currently only used in MapComponent).
 *
 * ARGUMENTS AGAINST THIS HOOK:
 * ============================
 * 1. **Over-Engineering**: Only used in one component (MapComponent), so the reusability
 *    benefit is theoretical rather than actual.
 *
 * 2. **Complexity**: Adds an extra layer of abstraction over basic map methods, making
 *    the code harder to follow and debug.
 *
 * 3. **Popup Closure Issues**: The automatic popup closure caused problems where map clicks
 *    would show popups briefly before they were immediately closed, requiring complex
 *    workarounds with `fromMapClick` flags.
 *
 * 4. **Not Much Code Saved**: The hook is essentially just wrapping `map.flyTo()` calls
 *    with `triggerClosePopups()` - not a significant amount of code to justify a hook.
 *
 * 5. **Less Explicit Control**: Makes it harder to understand when and why popups are
 *    being closed, as it's hidden inside the hook rather than explicit at call sites.
 *
 * ALTERNATIVE APPROACH:
 * ====================
 * Instead of this hook, we could:
 * 1. Use direct map methods (`map.flyTo()`, `mapRef.current.fitBounds()`, etc.)
 * 2. Call `triggerClosePopups()` explicitly only when needed for intentional navigation
 * 3. Remove the complex `fromMapClick` logic since popup closure would be explicit
 *
 * This would result in:
 * - More explicit, easier to understand code
 * - Better control over when popups are closed
 * - Fewer layers of abstraction
 * - Simpler debugging
 *
 * CURRENT STATUS:
 * ===============
 * The hook is working correctly after adding logic to distinguish between map clicks
 * and intentional navigation. However, the question remains whether the added complexity
 * is worth the benefits, especially since it's only used in one component.
 *
 * RECOMMENDATION:
 * ===============
 * Consider refactoring to remove this hook in favor of explicit popup management,
 * but only if there are no other pressing issues to address first.
 */

import { useCallback } from "react";
import { useUIActions } from "../store/UI";

interface MapFlyingOptions {
  center?: [number, number];
  zoom?: number;
  pitch?: number;
  bearing?: number;
  duration?: number;
  essential?: boolean;
  padding?: number;
  maxZoom?: number;
  animate?: boolean;
}

interface BoundsFlyingOptions {
  padding?: number;
  maxZoom?: number;
  duration?: number;
  animate?: boolean;
  pitch?: number;
  bearing?: number;
}

export const useMapFlying = (mapRef: React.RefObject<any>) => {
  const { triggerClosePopups } = useUIActions();

  const flyTo = useCallback(
    (options: MapFlyingOptions) => {
      if (!mapRef.current) return;

      console.log("[useMapFlying] Flying to location, closing popups");
      triggerClosePopups();

      const map = mapRef.current.getMap();
      map.flyTo({
        center: options.center,
        zoom: options.zoom || 14,
        pitch: options.pitch || 0,
        bearing: options.bearing || 0,
        duration: options.duration || 2000,
        essential: options.essential !== false,
        ...options
      });
    },
    [mapRef, triggerClosePopups]
  );

  const fitBounds = useCallback(
    (bounds: [[number, number], [number, number]], options?: BoundsFlyingOptions) => {
      if (!mapRef.current) return;

      console.log("[useMapFlying] Fitting to bounds, closing popups");
      triggerClosePopups();

      mapRef.current.fitBounds(bounds, {
        padding: options?.padding || 50,
        maxZoom: options?.maxZoom || 14,
        duration: options?.duration || 2000,
        animate: options?.animate !== false,
        pitch: options?.pitch || 0,
        bearing: options?.bearing || 0,
        ...options
      });
    },
    [mapRef, triggerClosePopups]
  );

  const easeTo = useCallback(
    (options: MapFlyingOptions) => {
      if (!mapRef.current) return;

      console.log("[useMapFlying] Easing to location, closing popups");
      triggerClosePopups();

      const map = mapRef.current.getMap();
      map.easeTo({
        center: options.center,
        zoom: options.zoom,
        pitch: options.pitch,
        bearing: options.bearing,
        duration: options.duration || 1000,
        ...options
      });
    },
    [mapRef, triggerClosePopups]
  );

  return {
    flyTo,
    fitBounds,
    easeTo
  };
};
